import "./grid-row/ch-grid-row";
import "./grid-rowset/ch-grid-rowset";
import "./grid-cell/ch-grid-cell";
import { Host, h } from "@stencil/core";
import { ChGridManager } from "./ch-grid-manager";
import { MouseEventButton, MouseEventButtons, focusComposedPath, mouseEventHasButtonPressed, mouseEventModifierKey } from "../common/helpers";
/**
 * The `ch-grid` component represents a Grid/TreeGrid of data, with rows and cells.
 */
export class ChGrid {
    constructor() {
        this.rowFocused = undefined;
        this.rowHighlighted = undefined;
        this.rowsMarked = [];
        this.rowsSelected = [];
        this.cellSelected = undefined;
        this.gridStyle = undefined;
        this.rowSelectionMode = "single";
        this.rowHighlightEnabled = "auto";
        this.rowSelectedClass = undefined;
        this.rowHighlightedClass = undefined;
        this.rowFocusedClass = undefined;
        this.rowMarkedClass = undefined;
        this.allowColumnReorder = true;
        this.columnResizeMode = "single";
        this.localization = undefined;
    }
    manager;
    gridLayoutElement;
    settingsUI;
    el;
    rowFocusedHandler(row, previous) {
        if (row) {
            row.focused = true;
        }
        if (previous) {
            previous.focused = false;
        }
    }
    rowHighlightedHandler(row, previous) {
        // highlight
        if (row) {
            row.highlighted = true;
        }
        if (previous) {
            previous.highlighted = false;
        }
        // actions
        if (row) {
            this.manager.rowActions.showOnRowHover?.openRowHover(row);
        }
        else {
            this.manager.rowActions.showOnRowHover?.close();
        }
    }
    rowsMarkedHandler(rows, previous) {
        this.manager.selection.syncRowSelector(rows, previous, "mark");
        this.rowMarkingChanged.emit({
            rowsId: rows.map(row => row.rowId),
            addedRowsId: rows
                .filter(row => !previous.includes(row))
                .map(row => row.rowId),
            removedRowsId: previous
                .filter(row => !rows.includes(row))
                .map(row => row.rowId),
            unalteredRowsId: rows
                .filter(row => previous.includes(row))
                .map(row => row.rowId)
        });
    }
    rowsSelectedHandler(rows, previous) {
        if (previous) {
            previous
                .filter(row => !rows.includes(row))
                .forEach(row => (row.selected = false));
        }
        if (rows) {
            rows.forEach(row => (row.selected = true));
        }
        this.manager.selection.syncRowSelector(rows, previous, "select");
        this.selectionChanged.emit({ rowsId: rows.map(row => row.rowId) });
    }
    cellSelectedHandler(cell, previous) {
        if (cell) {
            cell.selected = true;
        }
        if (previous) {
            previous.selected = false;
        }
        this.cellSelectionChanged.emit({
            cellId: this.cellSelected ? this.cellSelected.cellId : null,
            rowId: this.cellSelected ? this.cellSelected.row.rowId : null,
            columnId: this.cellSelected ? this.cellSelected.column.columnId : null
        });
    }
    /**
     * Event emitted when the row selection is changed.
     */
    selectionChanged;
    /**
     * Event emitted when the row marking is changed.
     */
    rowMarkingChanged;
    /**
     * Event emitted when the cell selection is changed.
     */
    cellSelectionChanged;
    /**
     * Event emitted when a row is clicked.
     */
    rowClicked;
    /**
     * Event emitted when a row is double clicked.
     */
    rowDoubleClicked;
    /**
     * Event emitted when Enter is pressed on a row.
     */
    rowEnterPressed;
    /**
     * Event emitted when attempts to open a context menu on a row.
     */
    rowContextMenu;
    componentWillLoad() {
        this.manager = new ChGridManager(this.el);
        this.gridStyle = this.manager.getGridStyle();
        this.rowsSelected = this.manager.getRowsSelected();
    }
    componentDidLoad() {
        this.manager.componentDidLoad(this.gridLayoutElement);
    }
    componentShouldUpdate(_newValue, _oldValue, name) {
        if (name === "rowFocused" ||
            name === "rowHighlighted" ||
            name === "rowSelected" ||
            name === "rowsMarked" ||
            name === "cellSelected") {
            return false;
        }
    }
    focusHandler() {
        if (this.rowSelectionMode !== "none") {
            this.rowFocused = this.rowsSelected[0] || this.manager.getFirstRow();
        }
    }
    blurHandler() {
        this.rowFocused = null;
    }
    cellFocusedHandler(eventInfo) {
        const cell = eventInfo.target;
        if (this.rowSelectionMode !== "none" && !cell.selected) {
            this.setCellSelected(cell);
        }
    }
    windowKeyDownHandler(eventInfo) {
        if (focusComposedPath()[0] === this.el &&
            [
                " ",
                "+",
                "-",
                "Home",
                "End",
                "PageUp",
                "PageDown",
                "ArrowUp",
                "ArrowDown",
                "ArrowLeft",
                "ArrowRight"
            ].includes(eventInfo.key)) {
            eventInfo.preventDefault();
        }
    }
    keyDownHandler(eventInfo) {
        if (focusComposedPath()[0] === this.el) {
            switch (eventInfo.key) {
                case " ":
                    this.toggleRowsMarked();
                    break;
                case "+":
                    this.setRowCollapsed(this.rowFocused, false);
                    break;
                case "-":
                    this.setRowCollapsed(this.rowFocused, true);
                    break;
                case "Home":
                    this.selectByKeyboardEvent(this.manager.selection.selectFirstRow.bind(this.manager.selection), eventInfo.shiftKey);
                    break;
                case "End":
                    this.selectByKeyboardEvent(this.manager.selection.selectLastRow.bind(this.manager.selection), eventInfo.shiftKey);
                    break;
                case "PageUp":
                    this.selectByKeyboardEvent(this.manager.selection.selectPreviousPageRow.bind(this.manager.selection), eventInfo.shiftKey);
                    break;
                case "PageDown":
                    this.selectByKeyboardEvent(this.manager.selection.selectNextPageRow.bind(this.manager.selection), eventInfo.shiftKey);
                    break;
                case "ArrowUp":
                    this.selectByKeyboardEvent(this.manager.selection.selectPreviousRow.bind(this.manager.selection), eventInfo.shiftKey);
                    break;
                case "ArrowDown":
                    this.selectByKeyboardEvent(this.manager.selection.selectNextRow.bind(this.manager.selection), eventInfo.shiftKey);
                    break;
                case "ArrowLeft":
                    this.selectByKeyboardEvent(this.manager.selection.selectPreviousCell.bind(this.manager.selection), eventInfo.shiftKey);
                    break;
                case "ArrowRight":
                    this.selectByKeyboardEvent(this.manager.selection.selectNextCell.bind(this.manager.selection), eventInfo.shiftKey);
                    break;
                case "Enter":
                    this.enterPressedHandler();
                    break;
            }
        }
    }
    mouseMoveHandler(eventInfo) {
        if ((this.rowHighlightEnabled === "auto" &&
            this.rowSelectionMode !== "none") ||
            this.rowHighlightEnabled === true) {
            this.rowHighlighted =
                this.manager.getRowEventTarget(eventInfo) ||
                    (this.manager.isRowActionsEventTarget(eventInfo)
                        ? this.rowHighlighted
                        : null);
        }
        selectingBlock: {
            if (this.manager.selection.selecting) {
                if (focusComposedPath()[0] !== this.el) {
                    this.stopSelecting();
                    break selectingBlock;
                }
                const row = this.manager.getRowEventTarget(eventInfo);
                const cell = this.manager.getCellEventTarget(eventInfo);
                if (row &&
                    (this.manager.selection.selectingRow !== row ||
                        this.manager.selection.selectingCell !== cell)) {
                    const isKeyModifierPressed = mouseEventModifierKey(eventInfo);
                    const isMouseButtonRightPressed = mouseEventHasButtonPressed(eventInfo, MouseEventButtons.RIGHT);
                    this.selectByPointerEvent(row, cell, isKeyModifierPressed && !isMouseButtonRightPressed, !isMouseButtonRightPressed, isMouseButtonRightPressed);
                    this.manager.selection.selectingRow = row;
                    this.manager.selection.selectingCell = cell;
                }
            }
        }
    }
    mouseLeaveHandler() {
        if (this.rowHighlighted) {
            this.rowHighlighted = null;
        }
    }
    clickHandler(eventInfo) {
        const row = this.manager.getRowEventTarget(eventInfo);
        const cell = this.manager.getCellEventTarget(eventInfo);
        if (row) {
            this.rowClicked.emit({
                rowId: row.rowId,
                cellId: cell?.cellId,
                columnId: cell?.column.columnId
            });
            this.manager.selection.selecting = true;
            this.selectByPointerEvent(row, cell, mouseEventModifierKey(eventInfo), eventInfo.shiftKey, eventInfo.button === MouseEventButton.RIGHT);
        }
    }
    mouseUpHandler() {
        this.stopSelecting();
    }
    dblclickHandler(eventInfo) {
        const row = this.manager.getRowEventTarget(eventInfo);
        const cell = this.manager.getCellEventTarget(eventInfo);
        if (row) {
            this.rowDoubleClicked.emit({
                rowId: row.rowId,
                cellId: cell?.cellId,
                columnId: cell?.column.columnId
            });
        }
    }
    contextmenuHandler(eventInfo) {
        let targetRow;
        if (eventInfo.target === this.el) {
            targetRow = this.rowFocused;
        }
        else {
            targetRow = this.manager.getRowEventTarget(eventInfo);
        }
        if (targetRow) {
            const cellFocused = this.cellSelected?.row === targetRow ? this.cellSelected : null;
            const rowContextMenuEventInfo = this.rowContextMenu.emit({
                rowId: targetRow.rowId,
                cellId: cellFocused?.cellId,
                columnId: cellFocused?.column.columnId,
                selectedRowsId: this.rowsSelected.map(row => row.rowId),
                clientX: eventInfo.clientX,
                clientY: eventInfo.clientY
            });
            this.manager.rowActions.showOnRowContext?.openRowContext(eventInfo.clientX, eventInfo.clientY);
            if (rowContextMenuEventInfo.defaultPrevented ||
                this.manager.rowActions.showOnRowContext) {
                eventInfo.preventDefault();
            }
        }
    }
    columnSelectorClickedHandler(eventInfo) {
        const columnSelector = this.manager.columns.getColumnSelector();
        if (columnSelector?.richRowSelectorMode === "select") {
            this.selectAll(eventInfo.detail.checked);
        }
        else if (columnSelector?.richRowSelectorMode === "mark") {
            this.rowsMarked = this.manager.selection.markAllRows(eventInfo.detail.checked);
        }
    }
    cellSelectorClickedHandler(eventInfo) {
        const columnSelector = this.manager.columns.getColumnSelector();
        if (columnSelector?.richRowSelectorMode === "select") {
            this.selectByPointerEvent(this.manager.getRowEventTarget(eventInfo), this.manager.getCellEventTarget(eventInfo), true, eventInfo.detail.range, false);
        }
        else if (columnSelector?.richRowSelectorMode === "mark") {
            this.rowsMarked = this.manager.selection.markRow(this.manager.getRowEventTarget(eventInfo), eventInfo.detail.checked, eventInfo.detail.range, this.rowsMarked);
        }
    }
    cellRowActionClickedHandler(eventInfo) {
        const cell = eventInfo.target;
        this.manager.rowActions.showOnRowActions?.openRowActions(cell);
    }
    columnStyleChangedHandler() {
        if (this.manager) {
            this.gridStyle = this.manager.getGridStyle();
        }
    }
    columnFreezeChangedHandler() {
        this.manager.columns.adjustFreezeOrder();
    }
    columnResizeStartedHandler(eventInfo) {
        this.manager.columnResizeStart(eventInfo.detail.columnId);
    }
    columnResizingHandler(eventInfo) {
        this.manager.columnResizing(eventInfo.detail.deltaWidth);
    }
    columnResizeFinishedHandler() {
        this.manager.columnResizeEnd();
    }
    columnDragStartHandler(eventInfo) {
        this.manager.columnDragStart(eventInfo.detail.columnId);
    }
    columnDraggingHandler(eventInfo) {
        if (this.manager.columnDragging(eventInfo.detail.positionX)) {
            this.gridStyle = this.manager.getGridStyle();
        }
    }
    columnDragEndHandler() {
        this.manager.columnDragEnd();
        this.gridStyle = this.manager.getGridStyle();
    }
    rowDragStartHandler(eventInfo) {
        this.manager.rowDragStart(eventInfo.detail.row);
    }
    rowEnsureVisibleHandler(eventInfo) {
        this.manager.ensureRowVisible(eventInfo.target);
    }
    cellEnsureVisibleHandler(eventInfo) {
        this.manager.ensureCellVisible(eventInfo.target);
    }
    settingsShowClickedHandler() {
        this.settingsUI.show = true;
    }
    settingsCloseClickedHandler() {
        this.settingsUI.show = false;
    }
    /**
     * Retrieves the rowId of the currently focused row.
     */
    async getFocusedRow() {
        return this.rowFocused?.rowId;
    }
    /**
     * Retrieves the rowId of the currently hovered row.
     */
    async getHoveredRow() {
        return this.rowHighlighted?.rowId;
    }
    /**
     * Retrieves the list of rowId of the selected rows.
     */
    async getSelectedRows() {
        return this.rowsSelected.map(row => row.rowId);
    }
    /**
     * Retrieves the list of rowId of the marked rows.
     */
    async getMarkedRows() {
        return this.rowsMarked.map(row => row.rowId);
    }
    /**
     * Retrieves information about the currently selected cell.
     */
    async getSelectedCell() {
        return {
            cellId: this.cellSelected ? this.cellSelected.cellId : null,
            rowId: this.cellSelected ? this.cellSelected.row.rowId : null,
            columnId: this.cellSelected ? this.cellSelected.column.columnId : null
        };
    }
    /**
     * Selects or deselects a row.
     * @param rowId - The rowId of the row to select or deselect.
     * @param selected - A boolean indicating whether to select or deselect the row.
     */
    async selectRow(rowId, selected = true) {
        const row = this.manager.getRow(rowId);
        if (row) {
            const { rowFocused, rowsSelected, cellSelected } = this.manager.selection.selectSet({
                rowFocused: this.rowFocused,
                rowsSelected: this.rowsSelected,
                cellSelected: this.cellSelected
            }, row, null, selected);
            this.rowFocused = rowFocused;
            this.rowsSelected = rowsSelected;
            this.cellSelected = cellSelected;
            rowFocused?.ensureVisible();
        }
    }
    /**
     * Selects or deselects all rows.
     * @param selected - A boolean indicating whether to select or deselect all rows.
     */
    async selectAllRows(selected = true) {
        this.selectAll(selected);
    }
    /**
     * Select or deselect a cell.
     * The cell can be identified by the cellId parameter or
     * by using the rowId and columnId pair.
     * @param cellId - The cellId of the cell to select or deselect.
     * @param rowId - The rowId of the row containing the cell.
     * @param columnId - The columnId of the column containing the cell.
     * @param selected - A boolean indicating whether to select or deselect the cell.
     */
    async selectCell(cellId, rowId, columnId, selected = true) {
        const cell = this.manager.getCell(cellId, rowId, columnId);
        if (cell) {
            this.setCellSelected(cell, selected);
        }
    }
    /**
     * Mark or unmark a row.
     * @param rowId - The rowId of the row to select or deselect.
     * @param marked - A boolean indicating whether to mark or unmark the row.
     */
    async markRow(rowId, marked = true) {
        const columnSelector = this.manager.columns.getColumnSelector();
        const row = this.manager.getRow(rowId);
        if (row && columnSelector?.richRowSelectorMode === "mark") {
            this.rowsMarked = this.manager.selection.markRow(row, marked, false, this.rowsMarked);
        }
    }
    /**
     * Mark or unmark all rows.
     * @param marked - A boolean indicating whether to mark or unmark all rows.
     */
    async markAllRows(marked = true) {
        const columnSelector = this.manager.columns.getColumnSelector();
        if (columnSelector?.richRowSelectorMode === "mark") {
            this.rowsMarked = this.manager.selection.markAllRows(marked);
        }
    }
    /**
     * Expands a row, showing its children.
     * @param rowId - The rowId of the row to expand.
     */
    async expandRow(rowId) {
        this.setRowCollapsed(this.manager.getRow(rowId), false);
    }
    /**
     * Collapses a row, hiding its children.
     * @param rowId - The rowId of the row to collapse.
     */
    async collapseRow(rowId) {
        this.setRowCollapsed(this.manager.getRow(rowId), true);
    }
    /**
     * Ensures that the row is visible within the control, scrolling the contents of the control if necessary.
     * @param rowId - The rowId of the row to ensure visibility.
     */
    async rowEnsureVisible(rowId) {
        const row = this.manager.getRow(rowId);
        if (row) {
            this.manager.ensureRowVisible(row);
        }
    }
    /**
     * Ensures that the cell is visible within the control, scrolling the contents of the control if necessary.
     * @param cellId - The cellId of the cell to ensure visibility.
     */
    async cellEnsureVisible(cellId) {
        const cell = this.manager.getCell(cellId);
        if (cell) {
            this.manager.ensureCellVisible(cell);
        }
    }
    /**
     * Retrieves the rowId of the previous row relative to the currently selected cell.
     */
    async getPreviousRow() {
        const currentRow = this.cellSelected?.row;
        if (currentRow) {
            return this.manager.getPreviousRow(currentRow)?.rowId || null;
        }
    }
    /**
     * Retrieves the rowId of the next row relative to the currently selected cell.
     */
    async getNextRow() {
        const currentRow = this.cellSelected?.row;
        if (currentRow) {
            return this.manager.getNextRow(currentRow)?.rowId || null;
        }
    }
    /**
     * Retrieves information about the previous cell relative to the currently selected cell.
     */
    async getPreviousCell() {
        const previousCell = this.manager.getPreviousCell(this.cellSelected);
        return {
            cellId: previousCell ? previousCell.cellId : null,
            rowId: previousCell ? previousCell.row.rowId : null,
            columnId: previousCell ? previousCell.column.columnId : null
        };
    }
    /**
     * Retrieves information about the next cell relative to the currently selected cell.
     */
    async getNextCell() {
        const nextCell = this.manager.getNextCell(this.cellSelected);
        return {
            cellId: nextCell ? nextCell.cellId : null,
            rowId: nextCell ? nextCell.row.rowId : null,
            columnId: nextCell ? nextCell.column.columnId : null
        };
    }
    enterPressedHandler() {
        if (this.rowFocused) {
            const cellFocused = this.cellSelected?.row === this.rowFocused ? this.cellSelected : null;
            this.rowEnterPressed.emit({
                rowId: this.rowFocused.rowId,
                cellId: cellFocused ? cellFocused.cellId : null,
                columnId: cellFocused ? cellFocused.column.columnId : null
            });
        }
    }
    toggleRowsMarked() {
        const columnSelector = this.manager.columns.getColumnSelector();
        if (columnSelector?.richRowSelectorMode === "mark") {
            const value = !this.rowFocused.marked;
            if (value) {
                this.rowsMarked = Array.from(new Set(this.rowsMarked.concat(this.rowsSelected)));
            }
            else {
                this.rowsMarked = this.rowsMarked.filter(row => !this.rowsSelected.includes(row));
            }
        }
    }
    selectByPointerEvent(row, cell, append, range, context) {
        const { rowFocused, rowsSelected, cellSelected } = this.manager.selection.select({
            rowFocused: this.rowFocused,
            rowsSelected: this.rowsSelected,
            cellSelected: this.cellSelected
        }, row, cell, append, range, context);
        this.rowFocused = rowFocused;
        this.rowsSelected = rowsSelected;
        this.cellSelected = cellSelected;
        if (cellSelected) {
            cellSelected.ensureVisible();
        }
        else {
            rowFocused?.ensureVisible();
        }
    }
    selectByKeyboardEvent(fn, append) {
        const { rowFocused, rowsSelected, cellSelected } = fn({
            rowFocused: this.rowFocused,
            rowsSelected: this.rowsSelected,
            cellSelected: this.cellSelected
        }, append);
        this.rowFocused = rowFocused;
        this.rowsSelected = rowsSelected;
        this.cellSelected = cellSelected;
        if (cellSelected) {
            cellSelected.ensureVisible();
        }
        else {
            rowFocused?.ensureVisible();
        }
    }
    selectAll(value = true) {
        const { rowFocused, rowsSelected, cellSelected } = this.manager.selection.selectAll({
            rowFocused: this.rowFocused,
            rowsSelected: this.rowsSelected,
            cellSelected: this.cellSelected
        }, value);
        this.rowFocused = rowFocused;
        this.rowsSelected = rowsSelected;
        this.cellSelected = cellSelected;
        if (cellSelected) {
            cellSelected.ensureVisible();
        }
        else {
            rowFocused?.ensureVisible();
        }
    }
    setRowCollapsed(row, collapsed) {
        if (row && collapsed) {
            if (row && row.hasChildRows) {
                row.collapsed = true;
            }
        }
        else if (row && !collapsed) {
            row.collapsed = false;
        }
    }
    setCellSelected(cell, selected = true) {
        const { rowFocused, rowsSelected, cellSelected } = this.manager.selection.selectSet({
            rowFocused: this.rowFocused,
            rowsSelected: this.rowsSelected,
            cellSelected: this.cellSelected
        }, cell.row, cell, selected);
        this.rowFocused = rowFocused;
        this.rowsSelected = rowsSelected;
        this.cellSelected = cellSelected;
        rowFocused?.ensureVisible();
    }
    stopSelecting() {
        this.manager.selection.selecting = false;
        this.manager.selection.selectingRow = null;
        this.manager.selection.selectingCell = null;
    }
    renderSettings() {
        return (h("ch-grid-settings", { grid: this.el, ref: el => (this.settingsUI = el), exportparts: "\n          mask:settings-mask,\n          window:settings-window,\n          header:settings-header,\n          caption:settings-caption,\n          close:settings-close,\n          main:settings-main,\n          footer:settings-footer\n        " }, h("slot", { name: "settings" }, h("ch-grid-settings-columns", { part: "settings-columns", columns: [...this.manager.getColumns()], exportparts: "\n              column:settings-columns-item,\n              column-label:settings-columns-label,\n              column-visible:settings-columns-visible,\n              column-visible-checked:settings-columns-visible-checked\n            " }))));
    }
    render() {
        return (h(Host, { tabindex: this.rowSelectionMode !== "none" ? "0" : false }, h("header", { part: "header" }, h("slot", { name: "header" })), h("section", { class: "main", style: this.gridStyle, part: "main", ref: el => (this.gridLayoutElement = el) }, h("slot", null)), h("aside", null, this.renderSettings(), h("slot", { name: "column-display" }), h("slot", { name: "row-actions" })), h("footer", { part: "footer" }, h("slot", { name: "footer" }))));
    }
    static get is() { return "ch-grid"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["ch-grid.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["ch-grid.css"]
        };
    }
    static get properties() {
        return {
            "rowSelectionMode": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"none\" | \"single\" | \"multiple\"",
                    "resolved": "\"multiple\" | \"none\" | \"single\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "One of \"none\", \"single\" or \"multiple\", indicating how rows can be selected.\nIt can be set to \"none\" if no rows should be selectable,\n\"single\" if only one row can be selected at a time, or\n\"multiple\" if multiple rows can be selected at once."
                },
                "attribute": "row-selection-mode",
                "reflect": false,
                "defaultValue": "\"single\""
            },
            "rowHighlightEnabled": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "boolean | \"auto\"",
                    "resolved": "\"auto\" | boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "One of \"false\", \"true\" or \"auto\", indicating whether or not rows can be highlighted.\n\"auto\", row highlighting will be enabled if the row selection mode is set to \"single\" or \"multiple\"."
                },
                "attribute": "row-highlight-enabled",
                "reflect": false,
                "defaultValue": "\"auto\""
            },
            "rowSelectedClass": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A CSS class name applied to a row when it is selected."
                },
                "attribute": "row-selected-class",
                "reflect": false
            },
            "rowHighlightedClass": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A CSS class name applied to a row when it is hovered."
                },
                "attribute": "row-highlighted-class",
                "reflect": false
            },
            "rowFocusedClass": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A CSS class name applied to a row when it is focused."
                },
                "attribute": "row-focused-class",
                "reflect": false
            },
            "rowMarkedClass": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A CSS class name applied to a row when it is marked."
                },
                "attribute": "row-marked-class",
                "reflect": false
            },
            "allowColumnReorder": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A boolean indicating whether the user can drag column headers to reorder columns."
                },
                "attribute": "allow-column-reorder",
                "reflect": false,
                "defaultValue": "true"
            },
            "columnResizeMode": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"single\" | \"splitter\"",
                    "resolved": "\"single\" | \"splitter\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "One of \"single\" or \"splitter\", indicating the behavior of column resizing.\n\"single\", resize a single column at a time.\n\"splitter\", when adjusts the width of one column, the neighboring columns\n   are also resized proportionally, maintaining the overall width."
                },
                "attribute": "column-resize-mode",
                "reflect": false,
                "defaultValue": "\"single\""
            },
            "localization": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "GridLocalization",
                    "resolved": "GridLocalization",
                    "references": {
                        "GridLocalization": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/grid/ch-grid.tsx",
                            "id": "src/components/grid/ch-grid.tsx::GridLocalization"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "An object that contains localized strings for the grid."
                }
            }
        };
    }
    static get states() {
        return {
            "rowFocused": {},
            "rowHighlighted": {},
            "rowsMarked": {},
            "rowsSelected": {},
            "cellSelected": {},
            "gridStyle": {}
        };
    }
    static get events() {
        return [{
                "method": "selectionChanged",
                "name": "selectionChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the row selection is changed."
                },
                "complexType": {
                    "original": "ChGridSelectionChangedEvent",
                    "resolved": "ChGridSelectionChangedEvent",
                    "references": {
                        "ChGridSelectionChangedEvent": {
                            "location": "import",
                            "path": "./ch-grid-types",
                            "id": "src/components/grid/ch-grid-types.ts::ChGridSelectionChangedEvent"
                        }
                    }
                }
            }, {
                "method": "rowMarkingChanged",
                "name": "rowMarkingChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the row marking is changed."
                },
                "complexType": {
                    "original": "ChGridMarkingChangedEvent",
                    "resolved": "ChGridMarkingChangedEvent",
                    "references": {
                        "ChGridMarkingChangedEvent": {
                            "location": "import",
                            "path": "./ch-grid-types",
                            "id": "src/components/grid/ch-grid-types.ts::ChGridMarkingChangedEvent"
                        }
                    }
                }
            }, {
                "method": "cellSelectionChanged",
                "name": "cellSelectionChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the cell selection is changed."
                },
                "complexType": {
                    "original": "ChGridCellSelectionChangedEvent",
                    "resolved": "ChGridCellSelectionChangedEvent",
                    "references": {
                        "ChGridCellSelectionChangedEvent": {
                            "location": "import",
                            "path": "./ch-grid-types",
                            "id": "src/components/grid/ch-grid-types.ts::ChGridCellSelectionChangedEvent"
                        }
                    }
                }
            }, {
                "method": "rowClicked",
                "name": "rowClicked",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when a row is clicked."
                },
                "complexType": {
                    "original": "ChGridRowClickedEvent",
                    "resolved": "ChGridRowClickedEvent",
                    "references": {
                        "ChGridRowClickedEvent": {
                            "location": "import",
                            "path": "./ch-grid-types",
                            "id": "src/components/grid/ch-grid-types.ts::ChGridRowClickedEvent"
                        }
                    }
                }
            }, {
                "method": "rowDoubleClicked",
                "name": "rowDoubleClicked",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when a row is double clicked."
                },
                "complexType": {
                    "original": "ChGridRowClickedEvent",
                    "resolved": "ChGridRowClickedEvent",
                    "references": {
                        "ChGridRowClickedEvent": {
                            "location": "import",
                            "path": "./ch-grid-types",
                            "id": "src/components/grid/ch-grid-types.ts::ChGridRowClickedEvent"
                        }
                    }
                }
            }, {
                "method": "rowEnterPressed",
                "name": "rowEnterPressed",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when Enter is pressed on a row."
                },
                "complexType": {
                    "original": "ChGridRowPressedEvent",
                    "resolved": "ChGridRowPressedEvent",
                    "references": {
                        "ChGridRowPressedEvent": {
                            "location": "import",
                            "path": "./ch-grid-types",
                            "id": "src/components/grid/ch-grid-types.ts::ChGridRowPressedEvent"
                        }
                    }
                }
            }, {
                "method": "rowContextMenu",
                "name": "rowContextMenu",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when attempts to open a context menu on a row."
                },
                "complexType": {
                    "original": "ChGridRowContextMenuEvent",
                    "resolved": "ChGridRowContextMenuEvent",
                    "references": {
                        "ChGridRowContextMenuEvent": {
                            "location": "import",
                            "path": "./ch-grid-types",
                            "id": "src/components/grid/ch-grid-types.ts::ChGridRowContextMenuEvent"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "getFocusedRow": {
                "complexType": {
                    "signature": "() => Promise<string>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string>"
                },
                "docs": {
                    "text": "Retrieves the rowId of the currently focused row.",
                    "tags": []
                }
            },
            "getHoveredRow": {
                "complexType": {
                    "signature": "() => Promise<string>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string>"
                },
                "docs": {
                    "text": "Retrieves the rowId of the currently hovered row.",
                    "tags": []
                }
            },
            "getSelectedRows": {
                "complexType": {
                    "signature": "() => Promise<string[]>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string[]>"
                },
                "docs": {
                    "text": "Retrieves the list of rowId of the selected rows.",
                    "tags": []
                }
            },
            "getMarkedRows": {
                "complexType": {
                    "signature": "() => Promise<string[]>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string[]>"
                },
                "docs": {
                    "text": "Retrieves the list of rowId of the marked rows.",
                    "tags": []
                }
            },
            "getSelectedCell": {
                "complexType": {
                    "signature": "() => Promise<{ cellId: string; rowId: string; columnId: string; }>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<{ cellId: string; rowId: string; columnId: string; }>"
                },
                "docs": {
                    "text": "Retrieves information about the currently selected cell.",
                    "tags": []
                }
            },
            "selectRow": {
                "complexType": {
                    "signature": "(rowId: string, selected?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "rowId",
                            "type": "string",
                            "docs": "- The rowId of the row to select or deselect."
                        }, {
                            "name": "selected",
                            "type": "boolean",
                            "docs": "- A boolean indicating whether to select or deselect the row."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Selects or deselects a row.",
                    "tags": [{
                            "name": "param",
                            "text": "rowId - The rowId of the row to select or deselect."
                        }, {
                            "name": "param",
                            "text": "selected - A boolean indicating whether to select or deselect the row."
                        }]
                }
            },
            "selectAllRows": {
                "complexType": {
                    "signature": "(selected?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "selected",
                            "type": "boolean",
                            "docs": "- A boolean indicating whether to select or deselect all rows."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Selects or deselects all rows.",
                    "tags": [{
                            "name": "param",
                            "text": "selected - A boolean indicating whether to select or deselect all rows."
                        }]
                }
            },
            "selectCell": {
                "complexType": {
                    "signature": "(cellId?: string, rowId?: string, columnId?: string, selected?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "cellId",
                            "type": "string",
                            "docs": "- The cellId of the cell to select or deselect."
                        }, {
                            "name": "rowId",
                            "type": "string",
                            "docs": "- The rowId of the row containing the cell."
                        }, {
                            "name": "columnId",
                            "type": "string",
                            "docs": "- The columnId of the column containing the cell."
                        }, {
                            "name": "selected",
                            "type": "boolean",
                            "docs": "- A boolean indicating whether to select or deselect the cell."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Select or deselect a cell.\nThe cell can be identified by the cellId parameter or\nby using the rowId and columnId pair.",
                    "tags": [{
                            "name": "param",
                            "text": "cellId - The cellId of the cell to select or deselect."
                        }, {
                            "name": "param",
                            "text": "rowId - The rowId of the row containing the cell."
                        }, {
                            "name": "param",
                            "text": "columnId - The columnId of the column containing the cell."
                        }, {
                            "name": "param",
                            "text": "selected - A boolean indicating whether to select or deselect the cell."
                        }]
                }
            },
            "markRow": {
                "complexType": {
                    "signature": "(rowId: string, marked?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "rowId",
                            "type": "string",
                            "docs": "- The rowId of the row to select or deselect."
                        }, {
                            "name": "marked",
                            "type": "boolean",
                            "docs": "- A boolean indicating whether to mark or unmark the row."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Mark or unmark a row.",
                    "tags": [{
                            "name": "param",
                            "text": "rowId - The rowId of the row to select or deselect."
                        }, {
                            "name": "param",
                            "text": "marked - A boolean indicating whether to mark or unmark the row."
                        }]
                }
            },
            "markAllRows": {
                "complexType": {
                    "signature": "(marked?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "marked",
                            "type": "boolean",
                            "docs": "- A boolean indicating whether to mark or unmark all rows."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Mark or unmark all rows.",
                    "tags": [{
                            "name": "param",
                            "text": "marked - A boolean indicating whether to mark or unmark all rows."
                        }]
                }
            },
            "expandRow": {
                "complexType": {
                    "signature": "(rowId: string) => Promise<void>",
                    "parameters": [{
                            "name": "rowId",
                            "type": "string",
                            "docs": "- The rowId of the row to expand."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Expands a row, showing its children.",
                    "tags": [{
                            "name": "param",
                            "text": "rowId - The rowId of the row to expand."
                        }]
                }
            },
            "collapseRow": {
                "complexType": {
                    "signature": "(rowId: string) => Promise<void>",
                    "parameters": [{
                            "name": "rowId",
                            "type": "string",
                            "docs": "- The rowId of the row to collapse."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Collapses a row, hiding its children.",
                    "tags": [{
                            "name": "param",
                            "text": "rowId - The rowId of the row to collapse."
                        }]
                }
            },
            "rowEnsureVisible": {
                "complexType": {
                    "signature": "(rowId: string) => Promise<void>",
                    "parameters": [{
                            "name": "rowId",
                            "type": "string",
                            "docs": "- The rowId of the row to ensure visibility."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Ensures that the row is visible within the control, scrolling the contents of the control if necessary.",
                    "tags": [{
                            "name": "param",
                            "text": "rowId - The rowId of the row to ensure visibility."
                        }]
                }
            },
            "cellEnsureVisible": {
                "complexType": {
                    "signature": "(cellId: string) => Promise<void>",
                    "parameters": [{
                            "name": "cellId",
                            "type": "string",
                            "docs": "- The cellId of the cell to ensure visibility."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Ensures that the cell is visible within the control, scrolling the contents of the control if necessary.",
                    "tags": [{
                            "name": "param",
                            "text": "cellId - The cellId of the cell to ensure visibility."
                        }]
                }
            },
            "getPreviousRow": {
                "complexType": {
                    "signature": "() => Promise<string>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string>"
                },
                "docs": {
                    "text": "Retrieves the rowId of the previous row relative to the currently selected cell.",
                    "tags": []
                }
            },
            "getNextRow": {
                "complexType": {
                    "signature": "() => Promise<string>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string>"
                },
                "docs": {
                    "text": "Retrieves the rowId of the next row relative to the currently selected cell.",
                    "tags": []
                }
            },
            "getPreviousCell": {
                "complexType": {
                    "signature": "() => Promise<{ cellId: string; rowId: string; columnId: string; }>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<{ cellId: string; rowId: string; columnId: string; }>"
                },
                "docs": {
                    "text": "Retrieves information about the previous cell relative to the currently selected cell.",
                    "tags": []
                }
            },
            "getNextCell": {
                "complexType": {
                    "signature": "() => Promise<{ cellId: string; rowId: string; columnId: string; }>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<{ cellId: string; rowId: string; columnId: string; }>"
                },
                "docs": {
                    "text": "Retrieves information about the next cell relative to the currently selected cell.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "rowFocused",
                "methodName": "rowFocusedHandler"
            }, {
                "propName": "rowHighlighted",
                "methodName": "rowHighlightedHandler"
            }, {
                "propName": "rowsMarked",
                "methodName": "rowsMarkedHandler"
            }, {
                "propName": "rowsSelected",
                "methodName": "rowsSelectedHandler"
            }, {
                "propName": "cellSelected",
                "methodName": "cellSelectedHandler"
            }];
    }
    static get listeners() {
        return [{
                "name": "focus",
                "method": "focusHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "blur",
                "method": "blurHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "cellFocused",
                "method": "cellFocusedHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "keydown",
                "method": "windowKeyDownHandler",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "keydown",
                "method": "keyDownHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "mousemove",
                "method": "mouseMoveHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "mouseleave",
                "method": "mouseLeaveHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "mousedown",
                "method": "clickHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "mouseup",
                "method": "mouseUpHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "dblclick",
                "method": "dblclickHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "contextmenu",
                "method": "contextmenuHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnSelectorClicked",
                "method": "columnSelectorClickedHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "cellSelectorClicked",
                "method": "cellSelectorClickedHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "cellRowActionClicked",
                "method": "cellRowActionClickedHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "columnHiddenChanged",
                "method": "columnStyleChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnOrderChanged",
                "method": "columnStyleChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnFreezeChanged",
                "method": "columnStyleChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnSizeChanging",
                "method": "columnStyleChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnFreezeChanged",
                "method": "columnFreezeChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnResizeStarted",
                "method": "columnResizeStartedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnResizing",
                "method": "columnResizingHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnResizeFinished",
                "method": "columnResizeFinishedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnDragStarted",
                "method": "columnDragStartHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnDragging",
                "method": "columnDraggingHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnDragEnded",
                "method": "columnDragEndHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "rowDragStarted",
                "method": "rowDragStartHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "rowEnsureVisible",
                "method": "rowEnsureVisibleHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "cellEnsureVisible",
                "method": "cellEnsureVisibleHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "settingsShowClicked",
                "method": "settingsShowClickedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "settingsCloseClicked",
                "method": "settingsCloseClickedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=ch-grid.js.map
